/**
 * Error Boundary — Display Client
 * ─────────────────────────────────────────────────────────────────────────────
 * Critical for 24/7 airport displays — prevents single component error from
 * blanking the entire screen. Auto-reloads after 30 seconds.
 */

import React from "react";
import * as Sentry from "@sentry/react";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorCount: 0 };
    this.reloadTimerRef = null;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo?.componentStack },
    });

    console.error("[ErrorBoundary] Caught display error:", error.message);

    // Auto-reload after 30s — displays should self-recover
    this.reloadTimerRef = setTimeout(() => {
      console.log("[ErrorBoundary] Auto-reloading display...");
      window.location.reload();
    }, 30000);
  }

  componentWillUnmount() {
    if (this.reloadTimerRef) clearTimeout(this.reloadTimerRef);
  }

  handleReset = () => {
    if (this.reloadTimerRef) clearTimeout(this.reloadTimerRef);
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "#0a0a14",
            color: "#e0e0e0",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "sans-serif",
            padding: "2rem",
            textAlign: "center",
          }}
          role="alert"
        >
          <div style={{ fontSize: "4rem", marginBottom: "1rem" }}>✈️</div>
          <h1 style={{ fontSize: "1.5rem", marginBottom: "0.5rem", color: "#f44336" }}>
            Display Temporarily Unavailable
          </h1>
          <p style={{ opacity: 0.7, marginBottom: "1.5rem", maxWidth: "500px" }}>
            The display encountered an error and will automatically recover in 30 seconds. Error has
            been reported to operations.
          </p>
          <button
            onClick={this.handleReset}
            style={{
              padding: "0.75rem 2rem",
              fontSize: "1rem",
              background: "#1976d2",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Retry Now
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
